import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Legend } from "recharts";
import { TrendingUp, Target, DollarSign, Clock, ChevronRight, FileText, Send, Eye, CheckCircle2 } from "lucide-react";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n || 0);

const STEP_ICONS = { generated: FileText, sent: Send, viewed: Eye, accepted: CheckCircle2 };
const STEP_COLORS = { generated: "#F5F5F5", sent: "#7BAEFA", viewed: "#D4AF37", accepted: "#4ADE80" };

export default function Analytics() {
  const { lang } = useApp();
  const [a, setA] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/analytics`).then(({ data }) => setA(data));
  }, []);

  if (!a) return <Spinner />;

  const T = lang === "fr" ? {
    title: "Analytique de conversion",
    sub: "Suivez l'entonnoir : généré → envoyé → consulté → accepté.",
    funnel: "Entonnoir de conversion",
    overall: "Conversion globale",
    win: "Taux de victoire",
    avg_deal: "Valeur moyenne acceptée",
    revenue: "Revenus acceptés",
    potential: "Pipeline ouvert",
    days: "Délai moyen de signature",
    days_unit: "jours",
    timeseries: "Évolution sur 6 mois",
    generated_label: "Générées",
    accepted_label: "Acceptées",
    revenue_label: "Revenus",
    no_data: "Pas encore de données — créez votre première soumission !",
    win_help: "Acceptées / (Acceptées + Refusées)",
    overall_help: "Acceptées / Générées",
    of_prev: "du précédent",
  } : {
    title: "Conversion analytics",
    sub: "Track the funnel: generated → sent → viewed → accepted.",
    funnel: "Conversion funnel",
    overall: "Overall conversion",
    win: "Win rate",
    avg_deal: "Avg deal size",
    revenue: "Accepted revenue",
    potential: "Open pipeline",
    days: "Avg time to close",
    days_unit: "days",
    timeseries: "6-month trend",
    generated_label: "Generated",
    accepted_label: "Accepted",
    revenue_label: "Revenue",
    no_data: "No data yet — create your first quote!",
    win_help: "Accepted / (Accepted + Rejected)",
    overall_help: "Accepted / Generated",
    of_prev: "of previous",
  };

  const maxFunnelCount = Math.max(...a.funnel.map(f => f.count), 1);

  return (
    <div data-testid="analytics-page">
      <PageHeader title={T.title} subtitle={T.sub} />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 max-w-6xl">
        <KpiTile icon={Target} color="#D4AF37" label={T.overall} value={`${a.overall_conversion}%`} help={T.overall_help} testid="kpi-overall" />
        <KpiTile icon={TrendingUp} color="#4ADE80" label={T.win} value={`${a.win_rate}%`} help={T.win_help} testid="kpi-win" />
        <KpiTile icon={DollarSign} color="#D4AF37" label={T.revenue} value={fmt(a.revenue_accepted)} testid="kpi-revenue" />
        <KpiTile icon={Clock} color="#7BAEFA" label={T.days} value={a.avg_days_to_close != null ? `${a.avg_days_to_close} ${T.days_unit}` : "—"} testid="kpi-days" />
      </div>

      {/* Funnel */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 sm:p-8 mb-6 max-w-6xl" data-testid="funnel-card">
        <h3 className="font-heading text-lg font-bold mb-1" style={{ color: "#D4AF37" }}>{T.funnel}</h3>
        <p className="text-xs mb-6" style={{ color: "rgba(245,245,245,0.5)" }}>{T.sub}</p>
        <div className="space-y-3">
          {a.funnel.map((f, i) => {
            const Icon = STEP_ICONS[f.step];
            const color = STEP_COLORS[f.step];
            const widthPct = Math.max(8, (f.count / maxFunnelCount) * 100);
            return (
              <motion.div key={f.step}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${color}1f`, border: `1px solid ${color}55` }}>
                  <Icon size={16} color={color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-semibold" style={{ color: "#F5F5F5" }}>
                      {lang === "fr" ? f.label_fr : f.label_en}
                    </span>
                    <span className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>
                      {i > 0 && `${f.rate_from_prev}% ${T.of_prev} · `}
                      <span className="font-heading text-base font-bold" style={{ color }}>{f.count}</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(245,245,245,0.06)" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${widthPct}%` }}
                      transition={{ duration: 0.6, delay: i * 0.08 }}
                      className="h-full rounded-full"
                      style={{ background: `linear-gradient(90deg, ${color}cc 0%, ${color} 100%)` }}
                    />
                  </div>
                </div>
                {i < a.funnel.length - 1 && (
                  <ChevronRight size={14} color="rgba(245,245,245,0.25)" className="flex-shrink-0 hidden sm:block" />
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Pipeline + revenue secondary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 max-w-6xl">
        <div className="glass-card p-5" data-testid="pipeline-card">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(245,245,245,0.5)" }}>{T.potential}</div>
          <div className="font-heading text-2xl font-bold" style={{ color: "#F5F5F5" }}>{fmt(a.revenue_potential)}</div>
          <div className="text-xs mt-1" style={{ color: "rgba(245,245,245,0.5)" }}>
            {lang === "fr" ? "Soumissions en attente de réponse" : "Quotes awaiting response"}
          </div>
        </div>
        <div className="glass-card p-5" data-testid="avg-deal-card">
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "rgba(245,245,245,0.5)" }}>{T.avg_deal}</div>
          <div className="font-heading text-2xl font-bold" style={{ color: "#D4AF37" }}>{fmt(a.avg_deal_size)}</div>
          <div className="text-xs mt-1" style={{ color: "rgba(245,245,245,0.5)" }}>
            {lang === "fr" ? `Sur ${a.funnel[3].count} soumission(s) acceptée(s)` : `Across ${a.funnel[3].count} accepted quote(s)`}
          </div>
        </div>
      </div>

      {/* Timeseries */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="glass-card p-6 sm:p-8 max-w-6xl mb-6" data-testid="timeseries-card">
        <h3 className="font-heading text-lg font-bold mb-4" style={{ color: "#D4AF37" }}>{T.timeseries}</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={a.timeseries}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(245,245,245,0.06)" />
              <XAxis dataKey="month" tick={{ fill: "rgba(245,245,245,0.5)", fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: "rgba(245,245,245,0.5)", fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#D4AF37", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "rgba(245,245,245,0.7)" }} />
              <Bar yAxisId="left" dataKey="generated" name={T.generated_label} fill="rgba(245,245,245,0.2)" radius={[6, 6, 0, 0]} />
              <Bar yAxisId="left" dataKey="accepted" name={T.accepted_label} fill="#4ADE80" radius={[6, 6, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" name={T.revenue_label} stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37", r: 4 }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    </div>
  );
}

function KpiTile({ icon: Icon, color, label, value, help, testid }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} color={color} />
        <div className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.55)" }}>{label}</div>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-black" style={{ color: "#F5F5F5" }}>{value}</div>
      {help && <div className="text-[10px] mt-1" style={{ color: "rgba(245,245,245,0.4)" }}>{help}</div>}
    </div>
  );
}
